/**
 * A dropdown menu. Closes on outside click, Escape, and selection; arrow keys
 * walk the items. Used for row actions, the user menu and export pickers.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from '../icons';

export interface MenuItem {
  key: string;
  label: ReactNode;
  icon?: IconName;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Renders a checked marker — for view/sort/theme pickers. */
  checked?: boolean;
  kbd?: string;
}

export function Menu({ trigger, items, align = 'right', placement = 'down', label }: {
  /** Render-prop for the trigger so the caller owns its styling. */
  trigger: (props: { open: boolean; toggle: () => void; ref: React.Ref<HTMLButtonElement> }) => ReactNode;
  items: (MenuItem | 'separator' | { label: string; heading: true })[];
  align?: 'left' | 'right';
  placement?: 'up' | 'down';
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const id = useId();

  const selectable = items.filter((i): i is MenuItem => typeof i === 'object' && 'onSelect' in i && !i.disabled);

  useEffect(() => {
    if (!open) return;
    function onDocDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => {
          const n = selectable.length;
          if (n === 0) return 0;
          return (c + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
        });
      } else if (e.key === 'Enter') {
        const item = selectable[cursor];
        if (item) {
          e.preventDefault();
          setOpen(false);
          item.onSelect();
        }
      }
    }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, cursor, selectable]);

  let selectableIndex = -1;

  return (
    <div className="menu" ref={wrapRef}>
      {trigger({
        open,
        toggle: () => {
          setOpen((v) => !v);
          setCursor(0);
        },
        ref: triggerRef,
      })}
      {open && (
        <div className={cx('menu-list', align === 'left' && 'left', placement === 'up' && 'up')} role="menu" aria-label={label} id={id}>
          {items.map((item, i) => {
            if (item === 'separator') return <div className="menu-sep" key={`sep-${i}`} />;
            if ('heading' in item) return <div className="menu-label" key={`h-${i}`}>{item.label}</div>;
            if (!item.disabled) selectableIndex += 1;
            const isCursor = !item.disabled && selectableIndex === cursor;
            const Glyph = item.icon ? Icon[item.icon] : null;
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                aria-selected={isCursor}
                aria-checked={item.checked}
                className={cx('menu-item', item.danger && 'danger')}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {Glyph && <Glyph aria-hidden />}
                {item.label}
                {item.kbd && <kbd className="kbd">{item.kbd}</kbd>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
